import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RequestStatus } from './network/managedRequest';
import {
    ACCOUNTING_AREA_ID_MARKER,
    processRequestForAccountingAreaWithReplacement,
} from './network/managedRequestForAccountingArea';
import { API } from '../constants';
import { PbDispatch, PbGetState } from './index';
import { pbRequestReset } from './pbRequestSlice';
import { PbRequest } from './pbRequest';

export interface SettlementBillingDetails {
    bank_data_available: boolean;
    account_holder_name?: string;
    iban?: string;
    bic?: string;

    auto_receive_credit_note: boolean;
    receive_credit_note_email?: string;
    credit_notes_enabled: boolean;
    last_changed_at?: number;
}

export const VATIN_VERIFICATION_STATUS_LIST = [
    'VALID',
    'INVALID',
    'DO_NOT_CHECK',
    'CHECK_REQUIRED',
    'TECHNICAL_ERROR',
    'CONFIRMED_BY_PARTNER',
] as const;

export type VatIdVerificationStatus =
    (typeof VATIN_VERIFICATION_STATUS_LIST)[number];


export function isValidVatIdVerificationStatus(status: VatIdVerificationStatus | undefined): boolean {
    if (!status) {
        return false;
    }

    const VALID_STATES : VatIdVerificationStatus[] = ['VALID', 'CONFIRMED_BY_PARTNER', 'DO_NOT_CHECK'];
    return VALID_STATES.includes(status);
}

export type VatIdVerificationErrorType =
    | 'API'
    | 'MISMATCHED_COMPANY_NAME'
    | 'COMPANY_WITHOUT_VAT';

export const CONTRACTUAL_TYPE_LIST = [
    'WWB',
    'WWC',
    'WWC_IDOS',
    'WWC_MINIMUM_USAGE',
    'WWC_IDOS_MINIMUM_USAGE',
    'RMS',
    'CITY_POP',
    'WHITE_LABEL_WWB',
    'WHITE_LABEL_WWC',
] as const;

export function isContractualTypeWhiteLabel(type: ContractualType): boolean {
    return type === 'WHITE_LABEL_WWB' || type === 'WHITE_LABEL_WWC';
}

export function isContractualTypePms(type: ContractualType): boolean {
    return type === 'RMS';
}

export type ContractualType = (typeof CONTRACTUAL_TYPE_LIST)[number];

export interface ContractDetails {
    vat_exempted: boolean;
    vat_exemption_reason?: string | null;
    vat_id?: string | null;
    vat_id_verification_status?: VatIdVerificationStatus;
    vat_id_verification_error_type?: VatIdVerificationErrorType;
    vat_id_verification_error?: string;
    tax_number?: string | null;
    contractual_wewash_share: number;
    contractual_type: ContractualType;
    tenant_id?: number | null;
    pms_provider?: string | null;
    company_registration_number?: string;
    last_changed_at?: number;
    country_id: number;
}

export interface ContractDetailsUpdateDto extends ContractDetails {
    confirm?: boolean;
}

interface UserDetailsStore {
    settlementBillingDetails: SettlementBillingDetails | undefined;
    contractDetails: ContractDetails | undefined;
}

const initialState: UserDetailsStore = {
    settlementBillingDetails: undefined,
    contractDetails: undefined,
} as const;

export const userDetailsSlice = createSlice({
    name: 'userDetails',
    initialState,
    reducers: {
        setSettlementBillingDetails(
            state: UserDetailsStore,
            action: PayloadAction<SettlementBillingDetails>
        ) {
            state.settlementBillingDetails = action.payload;
        },
        setContractDetails(
            state: UserDetailsStore,
            action: PayloadAction<ContractDetails>
        ) {
            state.contractDetails = action.payload;
        },
    },
});

export function ResetUserDetailsUpdates() {
    return async (dispatch: PbDispatch) => {
        dispatch(pbRequestReset(PbRequest.UPDATE_CONTRACT_DETAILS));
        dispatch(pbRequestReset(PbRequest.UPDATE_SETTLEMENT_BILLING_DETAILS));
    };
}

export function MaybeLoadContractDetails() {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        if (
            getState().pbRequest[PbRequest.FETCH_CONTRACT_DETAILS].status ===
            RequestStatus.INITIAL
        ) {
            await LoadContractDetails()(dispatch);
        }
    };
}

export function LoadContractDetails() {
    return async (dispatch: PbDispatch) => {
        const request: RequestInit = { method: 'GET' };
        const response =
            await processRequestForAccountingAreaWithReplacement<ContractDetails>(
                dispatch,
                `${API}/contract_details/${ACCOUNTING_AREA_ID_MARKER}`,
                request,
                PbRequest.FETCH_CONTRACT_DETAILS
            );
        dispatch(userDetailsSlice.actions.setContractDetails(response));
    };
}

export function UpdateContractDetails(data: ContractDetailsUpdateDto) {
    return async (dispatch: PbDispatch) => {
        try {
            const confirmString = data.confirm ? '?confirm=true' : '';
            const isPms = isContractualTypePms(data.contractual_type);
            const dataToSent = {
                vat_exempted: data.vat_exempted,
                vat_exemption_reason: data.vat_exempted
                    ? data.vat_exemption_reason
                    : null,
                vat_id: data.vat_id,
                tax_number: data.tax_number,
                contractual_wewash_share: data.contractual_wewash_share,
                contractual_type: data.contractual_type,
                tenant_id: data.tenant_id,
                pms_provider: isPms ? data.pms_provider : null,
                company_registration_number: data.company_registration_number,
            };
            const request: RequestInit = {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSent),
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/contract_details/${ACCOUNTING_AREA_ID_MARKER}${confirmString}`,
                request,
                PbRequest.UPDATE_CONTRACT_DETAILS
            );
            await LoadContractDetails()(dispatch);
        } catch {
            // already handled
        }
    };
}

export function LoadSettlementBillingDetails() {
    return async (dispatch: PbDispatch) => {
        const request: RequestInit = { method: 'GET' };
        const response =
            await processRequestForAccountingAreaWithReplacement<SettlementBillingDetails>(
                dispatch,
                `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/settlement_billing_details`,
                request,
                PbRequest.FETCH_SETTLEMENT_BILLING_DETAILS
            );
        dispatch(
            userDetailsSlice.actions.setSettlementBillingDetails(response)
        );
    };
}

export function UpdateSettlementBillingDetails(data: SettlementBillingDetails) {
    return async (dispatch: PbDispatch) => {
        try {
            const dataToSent: SettlementBillingDetails = {
                bank_data_available: data.bank_data_available,
                account_holder_name: data.bank_data_available
                    ? data.account_holder_name
                    : undefined,
                iban: data.bank_data_available ? data.iban : undefined,
                bic: data.bank_data_available ? data.bic : undefined,
                auto_receive_credit_note: data.auto_receive_credit_note,
                receive_credit_note_email: data.auto_receive_credit_note
                    ? data.receive_credit_note_email
                    : undefined,
                credit_notes_enabled: data.credit_notes_enabled,
            };
            const request: RequestInit = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSent),
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/settlement_billing_details`,
                request,
                PbRequest.UPDATE_SETTLEMENT_BILLING_DETAILS
            );
            await LoadSettlementBillingDetails()(dispatch);
        } catch {
            // already handled.
        }
    };
}

export function ConfirmVatinByPartner(vatin: string) {
    return async (dispatch: PbDispatch) => {
        try {
            const request: RequestInit = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ vat_id: vatin }),
            };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/contract_details/${ACCOUNTING_AREA_ID_MARKER}/confirm_vatin`,
                request,
                PbRequest.CONFIRM_VATIN_REQUEST
            );

            await LoadContractDetails()(dispatch);
        } catch {
            // should be managed
        }
    };
}

export default userDetailsSlice.reducer;
